/**
 * Terrain — procedural heightmap shaped to feel like Coffee County, TN
 * (Great Stage Park is a 700-acre rolling pasture cradled by gentle ridges).
 *
 * v1 ships with a noise-based height function so we can build the whole
 * world without external assets. To swap in a real USGS DEM later:
 *   1. Run scripts/build-heightmap.ts on a downloaded GeoTIFF
 *   2. Replace `HeightField.fromProcedural()` with `HeightField.fromR16(url)`
 *
 * Stage areas are deliberately flattened by `flattenAt()` calls in
 * world/Layout.ts so we don't put a stage on a hill.
 */

import * as THREE from 'three';
import { fbm2D } from '@/core/MathUtils';

export interface TerrainOptions {
  size: number;        // total side length in meters
  resolution: number;  // grid samples per side (e.g. 257)
  seed: number;
}

export interface HeightSample {
  height: number;
  normal: THREE.Vector3;
}

export class HeightField {
  readonly size: number;
  readonly resolution: number;
  readonly data: Float32Array;
  /** Real-world elevation min in meters (used for absolute height queries). */
  realMinElev = 0;
  realMaxElev = 0;
  private flatZones: { x: number; z: number; r: number; height: number }[] = [];

  constructor(size: number, resolution: number, data: Float32Array) {
    this.size = size;
    this.resolution = resolution;
    this.data = data;
  }

  /**
   * Load a heightmap baked from real USGS 3DEP elevation data.
   * Binary format: see scripts/fetch-terrain.mjs for the layout.
   */
  static async fromBinary(url: string, worldSize: number): Promise<HeightField> {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Failed to fetch heightmap: ${res.status} ${url}`);
    }
    const buf = await res.arrayBuffer();
    const view = new DataView(buf);
    const magic = view.getUint32(0, true);
    if (magic !== 0x54525256) {
      throw new Error(`Bad heightmap magic: 0x${magic.toString(16)}`);
    }
    const width = view.getUint16(4, true);
    const height = view.getUint16(6, true);
    if (width !== height) {
      throw new Error(`Heightmap must be square, got ${width}x${height}`);
    }
    const minElev = view.getFloat32(8, true);
    const maxElev = view.getFloat32(12, true);
    const range = maxElev - minElev;
    const u16 = new Uint16Array(buf, 16, width * height);
    const data = new Float32Array(width * height);

    // Render terrain in local coordinates centered on the festival, with the
    // mean elevation as the zero level. So a real-world 320m altitude becomes
    // local y=-3.9 if mean is 323.9m. This keeps numbers small and stages
    // place flat at y≈0.
    const meanElev = (minElev + maxElev) / 2;
    for (let i = 0; i < u16.length; i++) {
      const norm = u16[i] / 65535;
      const real = minElev + norm * range;
      data[i] = real - meanElev;
    }

    const hf = new HeightField(worldSize, width, data);
    hf.realMinElev = minElev;
    hf.realMaxElev = maxElev;
    return hf;
  }

  static fromProcedural(opts: TerrainOptions): HeightField {
    const { size, resolution, seed } = opts;
    const data = new Float32Array(resolution * resolution);
    const fbm = fbm2D(seed, 5);
    const ridgeFbm = fbm2D(seed + 17, 3);

    // Manchester TN sits around 320 m ASL with rolling pasture and a few low
    // ridges to the east. We render relative to local sea level, scaled
    // generously so the terrain reads on screen.
    for (let iy = 0; iy < resolution; iy++) {
      for (let ix = 0; ix < resolution; ix++) {
        const u = ix / (resolution - 1);
        const v = iy / (resolution - 1);
        const wx = (u - 0.5) * size;
        const wz = (v - 0.5) * size;

        // Big slow rolls
        const base = fbm(wx * 0.0015, wz * 0.0015) * 18;
        // Medium hummocks
        const med = fbm(wx * 0.006, wz * 0.006) * 4;
        // Tiny micro variation
        const micro = fbm(wx * 0.04, wz * 0.04) * 0.6;
        // A subtle ridge running NE
        const ridgeN = ridgeFbm(wx * 0.002 + wz * 0.001, wz * 0.002);
        const ridge = Math.max(0, ridgeN - 0.55) * 12;

        // Lower the center 600m radius to make a soft bowl, which is the
        // visual effect Bonnaroo's flat-pasture core gives.
        const dist = Math.sqrt(wx * wx + wz * wz);
        const bowl = -Math.exp(-dist * dist / (450 * 450)) * 6;

        data[iy * resolution + ix] = base + med + micro + ridge + bowl;
      }
    }
    return new HeightField(size, resolution, data);
  }

  /**
   * Mark a circular area to be flattened to a target height. Stage build
   * scripts call this to ensure they sit on level ground.
   */
  flattenAt(x: number, z: number, radius: number, height: number, falloff = 1.5): void {
    this.flatZones.push({ x, z, r: radius, height });
    const r2 = radius * radius;
    const half = this.size / 2;
    const step = this.size / (this.resolution - 1);
    const minIx = Math.max(0, Math.floor(((x - radius * falloff) + half) / step));
    const maxIx = Math.min(this.resolution - 1, Math.ceil(((x + radius * falloff) + half) / step));
    const minIz = Math.max(0, Math.floor(((z - radius * falloff) + half) / step));
    const maxIz = Math.min(this.resolution - 1, Math.ceil(((z + radius * falloff) + half) / step));
    for (let iz = minIz; iz <= maxIz; iz++) {
      for (let ix = minIx; ix <= maxIx; ix++) {
        const wx = -half + ix * step;
        const wz = -half + iz * step;
        const dx = wx - x;
        const dz = wz - z;
        const d2 = dx * dx + dz * dz;
        if (d2 > r2 * falloff * falloff) continue;
        const t = Math.min(1, Math.sqrt(d2) / radius);
        const blend = 1 - t * t * (3 - 2 * t);
        const idx = iz * this.resolution + ix;
        this.data[idx] = this.data[idx] * (1 - blend) + height * blend;
      }
    }
  }

  /** Bilinear sample of height at world XZ. */
  heightAt(x: number, z: number): number {
    const half = this.size / 2;
    const u = (x + half) / this.size;
    const v = (z + half) / this.size;
    if (u < 0 || u > 1 || v < 0 || v > 1) return 0;
    const fx = u * (this.resolution - 1);
    const fz = v * (this.resolution - 1);
    const ix = Math.floor(fx);
    const iz = Math.floor(fz);
    const tx = fx - ix;
    const tz = fz - iz;
    const i00 = iz * this.resolution + ix;
    const i10 = i00 + 1;
    const i01 = i00 + this.resolution;
    const i11 = i01 + 1;
    const a = this.data[i00] * (1 - tx) + this.data[i10] * tx;
    const b = this.data[i01] * (1 - tx) + this.data[i11] * tx;
    return a * (1 - tz) + b * tz;
  }

  /** Approximate surface normal via finite differences. */
  normalAt(x: number, z: number): THREE.Vector3 {
    const e = 1.0;
    const hL = this.heightAt(x - e, z);
    const hR = this.heightAt(x + e, z);
    const hD = this.heightAt(x, z - e);
    const hU = this.heightAt(x, z + e);
    return new THREE.Vector3(hL - hR, 2 * e, hD - hU).normalize();
  }
}

/* ------------------------------------------------------------------------- */
/* Terrain mesh                                                              */
/* ------------------------------------------------------------------------- */

export class Terrain {
  readonly heightField: HeightField;
  readonly mesh: THREE.Mesh;

  constructor(heightField: HeightField) {
    this.heightField = heightField;
    this.mesh = this.buildMesh();
  }

  static build(opts: TerrainOptions): Terrain {
    const hf = HeightField.fromProcedural(opts);
    return new Terrain(hf);
  }

  private buildMesh(): THREE.Mesh {
    const { size, resolution, data } = this.heightField;
    const geo = new THREE.PlaneGeometry(size, size, resolution - 1, resolution - 1);
    geo.rotateX(-Math.PI / 2);
    const positions = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < positions.count; i++) {
      positions.setY(i, data[i]);
    }
    geo.computeVertexNormals();

    // Vertex colors still blend slope/height, shader layers grass noise on top
    const colors = new Float32Array(positions.count * 3);
    const grass = new THREE.Color('#5b8a28');
    const dryGrass = new THREE.Color('#bba84a');
    const dirt = new THREE.Color('#7a5028');
    const tmp = new THREE.Color();
    for (let i = 0; i < positions.count; i++) {
      const y = positions.getY(i);
      const ny = (geo.attributes.normal as THREE.BufferAttribute).getY(i);
      const slope = 1 - ny;
      const high = Math.min(1, Math.max(0, (y - 8) / 12));
      tmp.copy(grass).lerp(dryGrass, high * 0.7);
      tmp.lerp(dirt, Math.min(1, slope * 1.6));
      colors[i * 3 + 0] = tmp.r;
      colors[i * 3 + 1] = tmp.g;
      colors[i * 3 + 2] = tmp.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    // Build a procedural grass texture on a canvas. Tile it many times so
    // individual blades are readable at walking-distance scale.
    const grassTex = Terrain.buildGrassCanvasTexture();
    grassTex.wrapS = THREE.RepeatWrapping;
    grassTex.wrapT = THREE.RepeatWrapping;
    grassTex.repeat.set(180, 180); // one tile ≈ 12m across a 2200m terrain
    grassTex.colorSpace = THREE.SRGBColorSpace;
    grassTex.anisotropy = 4;

    // A second larger-scale noise map for macro color variation
    const macroTex = Terrain.buildMacroNoiseTexture();
    macroTex.wrapS = THREE.RepeatWrapping;
    macroTex.wrapT = THREE.RepeatWrapping;
    macroTex.repeat.set(6, 6);

    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.93,
      metalness: 0.0,
      map: grassTex
    });

    // Custom fragment that blends a micro grass texture with macro noise
    // patches while preserving the vertex color base.
    const uMacro = { value: macroTex };
    (mat.userData as Record<string, unknown>).uMacro = uMacro;

    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uMacro = uMacro;
      shader.fragmentShader = shader.fragmentShader.replace(
        'uniform vec3 diffuse;',
        `
          uniform vec3 diffuse;
          uniform sampler2D uMacro;
        `
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <map_fragment>',
        `
          #ifdef USE_MAP
            vec4 texel = texture2D(map, vMapUv);
            vec4 macro = texture2D(uMacro, vMapUv / 30.0);
            float lum = (texel.r + texel.g + texel.b) / 3.0;
            // Macro noise shifts the base color across big patches
            vec3 macroTinted = diffuseColor.rgb * mix(0.8, 1.25, macro.g);
            // Micro grass texture applied on top
            vec3 combined = macroTinted * (0.4 + texel.rgb * 1.4);
            diffuseColor.rgb = combined;
            diffuseColor.rgb = clamp(diffuseColor.rgb, 0.0, 2.0);
          #endif
        `
      );
    };

    const mesh = new THREE.Mesh(geo, mat);
    mesh.receiveShadow = true;
    mesh.name = 'Terrain';
    return mesh;
  }

  /**
   * Procedural grass texture: scattered blade strokes + flower specks.
   * Generated once per terrain build and tiled across the mesh.
   */
  private static buildGrassCanvasTexture(): THREE.CanvasTexture {
    const size = 512;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    // Base wash: warm varied greens
    const baseGrad = ctx.createLinearGradient(0, 0, size, size);
    baseGrad.addColorStop(0, '#4a7a1e');
    baseGrad.addColorStop(0.25, '#5f9128');
    baseGrad.addColorStop(0.55, '#6fa22e');
    baseGrad.addColorStop(0.8, '#558818');
    baseGrad.addColorStop(1, '#6a9b26');
    ctx.fillStyle = baseGrad;
    ctx.fillRect(0, 0, size, size);

    // Big dark grass clumps
    for (let i = 0; i < 80; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = 8 + Math.random() * 22;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, 'rgba(30, 70, 20, 0.6)');
      g.addColorStop(1, 'rgba(30, 70, 20, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // Lighter patches (sun-bleached grass)
    for (let i = 0; i < 60; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = 10 + Math.random() * 25;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, 'rgba(200, 190, 90, 0.45)');
      g.addColorStop(1, 'rgba(200, 190, 90, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // Dense grass-blade strokes — more + bolder
    for (let i = 0; i < 5500; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const len = 4 + Math.random() * 10;
      const angle = (Math.random() - 0.5) * 0.5;
      const r = Math.floor(40 + Math.random() * 50);
      const g = 110 + Math.floor(Math.random() * 110);
      const b = 30 + Math.floor(Math.random() * 30);
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.55 + Math.random() * 0.35})`;
      ctx.lineWidth = 1 + Math.random() * 1.5;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.sin(angle) * len, y - Math.cos(angle) * len);
      ctx.stroke();
    }

    // Scattered flower specks
    const flowerColors = [
      'rgba(255,220,120,1.0)',
      'rgba(255,255,220,1.0)',
      'rgba(240,180,220,0.95)',
      'rgba(255,160,120,0.95)',
      'rgba(220,180,255,0.95)'
    ];
    for (let i = 0; i < 350; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      ctx.fillStyle = flowerColors[Math.floor(Math.random() * flowerColors.length)];
      ctx.beginPath();
      ctx.arc(x, y, 1.4 + Math.random() * 1.6, 0, Math.PI * 2);
      ctx.fill();
    }

    // Dirt patches (trampled areas)
    for (let i = 0; i < 100; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = 8 + Math.random() * 18;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, 'rgba(130, 85, 35, 0.55)');
      g.addColorStop(1, 'rgba(130, 85, 35, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.needsUpdate = true;
    return tex;
  }

  /**
   * Macro-scale green variation — a soft blobby noise used to shift grass
   * color across whole regions so the terrain doesn't look monotone.
   */
  private static buildMacroNoiseTexture(): THREE.CanvasTexture {
    const size = 256;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, size, size);
    for (let i = 0; i < 160; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = 16 + Math.random() * 50;
      const shade = 90 + Math.floor(Math.random() * 150);
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(${shade}, ${shade}, ${shade}, 0.6)`);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.needsUpdate = true;
    return tex;
  }

  heightAt(x: number, z: number): number {
    return this.heightField.heightAt(x, z);
  }

  normalAt(x: number, z: number): THREE.Vector3 {
    return this.heightField.normalAt(x, z);
  }

  /** Rebuild mesh after flattening operations. */
  rebuild(): void {
    const positions = this.mesh.geometry.attributes.position as THREE.BufferAttribute;
    const data = this.heightField.data;
    for (let i = 0; i < positions.count; i++) {
      positions.setY(i, data[i]);
    }
    positions.needsUpdate = true;
    this.mesh.geometry.computeVertexNormals();
  }
}
